/**
 * Worlds (CDEV-2028): one noun for a property.
 *
 * A world is one property (Big Fish, Cavali, a fork). The rail's Worlds entry
 * lists the surfaces of the world in focus, and switching worlds changes the
 * context, not the place: Board in Big Fish becomes Board in Cavali. The URL
 * carries the world, `/worlds/$world/$surface`; drill-downs sit under the
 * surface they belong to (`/worlds/$world/characters/$slug`).
 */

export type WorldSurface = 'script' | 'board' | 'characters' | 'locations' | 'storyboards' | 'sequences' | 'assets';

/**
 * Authoring a world's content goes through constructs; arranging the Board has
 * its own verbs. `composeAny` is a surface's write gate: any of these puts
 * COMPOSE in its intent scope (CDEV-2029, ARCH-NOTE-018 STK-2). That intent
 * scope is capability scope is the note's hypothesis.
 */
const AUTHOR_CAPS = ['construct.create', 'construct.update'];

export const WORLD_SURFACES: readonly { id: WorldSurface; label: string; composeAny: readonly string[] }[] = [
  { id: 'script', label: 'Script', composeAny: AUTHOR_CAPS },
  { id: 'board', label: 'Board', composeAny: ['board.arrange', 'board.restructure', 'board.propose'] },
  { id: 'characters', label: 'Characters', composeAny: AUTHOR_CAPS },
  { id: 'locations', label: 'Locations', composeAny: AUTHOR_CAPS },
  { id: 'storyboards', label: 'Storyboards', composeAny: AUTHOR_CAPS },
  { id: 'sequences', label: 'Sequences', composeAny: AUTHOR_CAPS },
  { id: 'assets', label: 'Assets', composeAny: AUTHOR_CAPS },
];

export const DEFAULT_WORLD_SURFACE: WorldSurface = 'script';

export const WORLDS_INDEX = '/worlds';

export function isWorldSurface(value: string): value is WorldSurface {
  return WORLD_SURFACES.some((s) => s.id === value);
}

export function worldSurfaceHref(worldId: string, surface: WorldSurface = DEFAULT_WORLD_SURFACE): string {
  return `${WORLDS_INDEX}/${encodeURIComponent(worldId)}/${surface}`;
}

export interface WorldLocation {
  worldId: string;
  /** The surface the path is on or under; null on `/worlds/$world` itself or an unknown segment. */
  surface: WorldSurface | null;
}

/** Where a path sits among the worlds, or null when it is not a world path. */
export function parseWorldPath(path: string): WorldLocation | null {
  const m = /^\/worlds\/([^/]+)(?:\/([^/]+))?/.exec(path);
  if (!m) return null;
  const segment = m[2] ?? '';
  return { worldId: decodeURIComponent(m[1]), surface: isWorldSurface(segment) ? segment : null };
}

/**
 * Where switching to `worldId` lands from `currentPath`: the same surface in
 * the new world. A drill-down (one character) has no counterpart there, so it
 * lands on its surface's list; anywhere else lands on the default surface.
 */
export function switchWorldHref(currentPath: string, worldId: string): string {
  const here = parseWorldPath(currentPath);
  return worldSurfaceHref(worldId, here?.surface ?? DEFAULT_WORLD_SURFACE);
}
